import type { BedPricingItem, BedResumePaymentState } from '../../lib/bed/draft';
import {
  computeEngagementBreakdown,
  computeSettlementLines,
  formatCents,
  formatDueDateFr,
} from '../../lib/bed/wizard-model';
import type { BedRecapAmounts } from './BedRecapStep';

/**
 * 00252 (retours 19, 21, 23 — call 4L du 08/09/2026) : LE tableau des montants,
 * partagé par l'étape ⑤ (vivant : il suit les options cochées) et le
 * récapitulatif ⑥ (figé). Même mise en forme aux deux endroits, c'est la
 * demande : « le format brut, montant de l'engagement moins l'acompte, puis
 * les options qui font évoluer le total ».
 */
export function BedAmountsTable({
  amounts,
  mandatoryItems,
  selectedOptions,
  totalCents,
  variant,
  balanceDeadline = null,
  resumeState = null,
  optionsUnavailable = false,
  testId,
}: {
  amounts: BedRecapAmounts | null;
  /** Retour 24 : postes obligatoires de la grille, détaillés et toujours affichés. */
  mandatoryItems: BedPricingItem[];
  selectedOptions: BedPricingItem[];
  totalCents: number;
  variant: 'live' | 'frozen';
  /** Retour 21 : date butoir de paiement du solde (null = mention générique). */
  balanceDeadline?: string | null;
  /**
   * État du dossier de paiement (00240), s'il existe : son restant dû fait foi
   * pour « Reste à régler » (revue du 23/09, point 7).
   */
  resumeState?: BedResumePaymentState | null;
  /** 00296 : aucune option ne peut être prise en ligne (solde couvert, sans dossier). */
  optionsUnavailable?: boolean;
  testId?: string;
}) {
  if (amounts === null) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Le montant de votre solde n’a pas pu être calculé automatiquement. Votre bulletin reste
        enregistrable : notre service administratif reprendra contact avec vous.
      </p>
    );
  }
  // Recette du 22/09 (retour 35) : « Montant de votre engagement » HORS
  // souscriptions obligatoires, puis leur détail. Le poste d'engagement se
  // reconnaît à son code de grille (00273) ; sans lui, aucune décomposition
  // (revue du 23/09, point 1). La somme ne bouge pas d'un centime.
  const { engagementCents, subscriptions } = computeEngagementBreakdown(
    amounts.prixTotalCents,
    mandatoryItems
  );
  const settlement = computeSettlementLines({
    totalCents,
    paiementsPartielsCents: amounts.paiementsPartielsCents,
    resumeState,
  });
  const deadlineLabel = balanceDeadline ? formatDueDateFr(balanceDeadline) : '';
  return (
    <div className="space-y-2 rounded-lg border border-zinc-200 p-4" data-testid={testId}>
      <AmountRow label="Montant de votre engagement" value={engagementCents} />
      <AmountRow
        label="Acompte versé au titre de votre pré-inscription"
        value={amounts.acompteCents}
        negative
      />
      {amounts.reductionCents > 0 && (
        <AmountRow
          label={amounts.reductionLabel ? `Réduction — ${amounts.reductionLabel}` : 'Réduction'}
          value={amounts.reductionCents}
          negative
        />
      )}
      {/* Retour 24 : le pack sécurité et l'assistance rapatriement sont
          obligatoires — ils figurent toujours, détaillés. */}
      {subscriptions.length > 0 && (
        <div className="space-y-1 border-t border-zinc-100 pt-2">
          <p className="text-sm font-semibold text-zinc-900">Souscriptions obligatoires</p>
          {subscriptions.map((item) => (
            <AmountRow
              key={item.pricing_item_id}
              label={item.label}
              value={item.amount_cents}
              indented
            />
          ))}
        </div>
      )}

      <div className="space-y-1 border-t border-zinc-100 pt-2">
        <p className="text-sm font-semibold text-zinc-900">Options</p>
        {selectedOptions.length > 0 ? (
          selectedOptions.map((item) => (
            <AmountRow
              key={item.pricing_item_id}
              label={item.label}
              value={item.amount_cents}
              indented
            />
          ))
        ) : variant === 'live' && !optionsUnavailable ? (
          <p className="text-xs text-zinc-500">
            Aucune option choisie pour l’instant — cochez-en ci-dessous, le total se met à jour.
          </p>
        ) : (
          <p className="text-xs text-zinc-500">Aucune option retenue.</p>
        )}
      </div>

      <div className="space-y-1 border-t border-zinc-200 pt-3">
        <AmountRow
          label={
            variant === 'live' && !optionsUnavailable
              ? 'TOTAL À RÉGLER (mis à jour selon vos choix)'
              : 'TOTAL À RÉGLER'
          }
          value={Math.max(totalCents, 0)}
          emphasis
        />
        {/* Recette du 07/10 (Floran) : acompte et réduction dépassent le total
            (engagement offert, trop versé) : 0 à régler, et le surplus dit. */}
        {totalCents < 0 && (
          <AmountRow label="Surplus (acompte et réduction au-delà du total)" value={-totalCents} />
        )}
        {/* Retour 35 (« tout paiement partiel engagé ») et revue du 23/09,
            points 7 et 8 : le déjà-versé se lit SOUS le total, et TOTAL − déjà
            versé = reste. Le reste est celui du dossier quand il existe : c'est
            ce que le paiement encaissera. */}
        {settlement && (
          <>
            {settlement.paidCents > 0 && (
              <AmountRow
                label="Paiements partiels déjà versés"
                value={settlement.paidCents}
                negative
              />
            )}
            <AmountRow
              label="Reste à régler après vos versements"
              value={settlement.remainingCents}
              emphasis
            />
          </>
        )}
      </div>

      {variant === 'frozen' && (
        <p className="border-t border-zinc-100 pt-3 text-xs text-zinc-700">
          <strong>Date butoir de paiement du solde :</strong>{' '}
          {deadlineLabel ? deadlineLabel : 'celle communiquée par l’organisation'}.{' '}
          <strong>
            Votre équipage n’est inscrit définitivement qu’une fois le paiement total et
            l’intégralité de vos documents reçus.
          </strong>
        </p>
      )}
    </div>
  );
}

export function AmountRow({
  label,
  value,
  emphasis = false,
  negative = false,
  indented = false,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
  negative?: boolean;
  /** Ligne de détail sous un intitulé de bloc (maquette 4L du 22/09). */
  indented?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 ${
        emphasis ? 'text-base font-semibold text-zinc-900' : 'text-sm text-zinc-700'
      } ${indented ? 'pl-4' : ''}`}
    >
      <span>{label}</span>
      <span className="tabular-nums">
        {negative ? '− ' : ''}
        {formatCents(value)}
      </span>
    </div>
  );
}
